import React from "react";

const C = {
  surface: "rgba(255,255,255,0.025)",
  border: "rgba(255,255,255,0.07)",
  indigo: "#6366F1",
  violet: "#7C3AED",
  text: "#F8F7FF",
  muted: "rgba(148,163,184,0.7)",
  green: "#10B981",
  amber: "#F59E0B",
};

const PREVIEW_SCORES = [
  { label: "Market Size", score: 90, color: C.green },
  { label: "Timing", score: 78, color: C.green },
  { label: "Competition", score: 65, color: C.amber },
  { label: "Monetization", score: 82, color: C.green },
  { label: "Defensibility", score: 68, color: C.amber },
];

const STEPS = [
  { n: 1, icon: "describe", title: "Describe your idea", text: "Write a few sentences about your startup concept. No pitch deck needed.", accent: C.indigo },
  { n: 2, icon: "analyze", title: "AI analyzes it", text: "Our AI scores market size, timing, competition, monetization, and defensibility.", accent: C.violet },
  { n: 3, icon: "verdict", title: "Get your verdict", text: "Receive Fund It, Needs Work, or Hard Pass with detailed reasoning from both sides.", accent: C.green },
];

const LAYER2_TOOLS = [
  {
    icon: "🔀",
    title: "Pivot Suggestor",
    text: "AI proposes 3 stronger versions of your idea, targeting your weakest score dimensions.",
    plan: "Free",
  },
  {
    icon: "🎯",
    title: "ICP Generator",
    text: "Define your ideal customer profile, pain points, channels, and a one-line messaging hook.",
    plan: "Starter",
  },
  {
    icon: "🗺️",
    title: "Competitor Mapper",
    text: "Map direct and indirect competitors, white-space opportunities, and a differentiation playbook.",
    plan: "Pro",
  },
  {
    icon: "🚀",
    title: "Landing Page Generator",
    text: "Generate MVP landing page copy and a live HTML preview you can ship today.",
    plan: "Pro",
  },
  {
    icon: "📊",
    title: "Pitch Deck Builder",
    text: "Get a 10-slide investor deck outline with speaker notes, grounded in your validation scores.",
    plan: "Pro",
  },
];

const TESTIMONIALS = [
  { quote: "Saved me 6 months of work. The competition score was spot-on — I pivoted before writing a single line of code.", name: "Alex K.", role: "2× founder, Berlin", color: "#6366F1" },
  { quote: "Used this before my YC application. The killer insight section alone was worth it. Got into the batch.", name: "Maria R.", role: "YC S24 applicant", color: C.green },
  { quote: "Brutally honest in the best way. Told me my idea was a Hard Pass — and it was right. Started something better.", name: "James P.", role: "Solo builder, London", color: C.amber },
];

const FAQ = [
  {
    q: "How does AI Startup Validator work?",
    a: "Describe your startup idea in a few sentences. Our AI analyzes it across five VC dimensions — market size, timing, competition, monetization, and defensibility — and returns a verdict with bull case, bear case, and a killer insight in about 8 seconds.",
  },
  {
    q: "Is startup idea validation free?",
    a: "Yes. Visitors can run a few built-in demo validations with no signup and no AI usage, so you can see the full scoring flow first. Every account then includes 3 live AI validations with full access to all five score dimensions, bull and bear analysis, and the killer insight. No credit card is required to start.",
  },
  {
    q: "What dimensions does the AI score?",
    a: "Market size, timing, competition, monetization, and defensibility — the same lenses a venture capitalist uses to evaluate early-stage startups.",
  },
  {
    q: "Can I use this before applying to YC or raising funding?",
    a: "Absolutely. Founders use AI Startup Validator to stress-test ideas before YC applications, investor meetings, and major product pivots.",
  },
  {
    q: "How is this different from asking ChatGPT?",
    a: "Unlike a generic chatbot, we use a structured VC scoring framework with calibrated dimensions, consistent verdicts (Fund It, Needs Work, Hard Pass), and actionable killer insights tailored for founders.",
  },
  {
    q: "What is Layer 2 — Idea Development?",
    a: "After a Needs Work or Hard Pass verdict, Layer 2 unlocks five AI tools to improve your idea: Pivot Suggestor, ICP Generator, Competitor Mapper, Landing Page Generator, and Pitch Deck Builder. They use your validation scores and killer insight as context — not generic advice.",
  },
  {
    q: "What happens if my idea gets Needs Work?",
    a: "You still get the full bull case, bear case, and killer insight. Then Layer 2 opens with tools to pivot, define your ICP, map competitors, draft a landing page, or build a pitch deck. Pivot Suggestor is free; other tools are included on Starter and Pro plans.",
  },
];

const PRICING = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    features: ["3 validations total", "All 5 score dimensions", "Bull & Bear analysis", "Pivot Suggestor (Layer 2)"],
    cta: "Get started free",
    popular: false,
  },
  {
    name: "Pro",
    price: "$29",
    period: "/mo",
    features: ["Unlimited validations", "Full Layer 2 toolkit", "Competitor Mapper & Pitch Deck", "Landing Page Generator", "PDF export & API access"],
    cta: "Start 7-day trial",
    popular: true,
  },
  {
    name: "Team",
    price: "$99",
    period: "/mo",
    features: ["5 team seats", "Unlimited validations", "White-label reports", "Slack integration", "Dedicated CSM"],
    cta: "Start team trial",
    popular: false,
  },
];

function SectionLabel({ children }) {
  return (
    <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", color: C.muted, marginBottom: 12 }}>
      {children}
    </p>
  );
}

function GhostBtn({ children, onClick, primary }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "14px 28px",
        borderRadius: 12,
        border: `1px solid ${primary ? "rgba(255,255,255,0.25)" : C.border}`,
        background: primary ? "rgba(255,255,255,0.06)" : "transparent",
        color: C.text,
        fontSize: 15,
        fontWeight: primary ? 600 : 500,
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

function ScoreBar({ label, score, color }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
        <span style={{ color: C.muted }}>{label}</span>
        <span style={{ fontWeight: 600 }}>{score}</span>
      </div>
      <div style={{ height: 6, borderRadius: 99, background: "rgba(255,255,255,0.06)" }}>
        <div style={{ width: `${score}%`, height: "100%", borderRadius: 99, background: color }} />
      </div>
    </div>
  );
}

function StepIcon({ type, size = 22 }) {
  const props = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.75, strokeLinecap: "round", strokeLinejoin: "round" };

  if (type === "describe") {
    return (
      <svg {...props}>
        <path d="M14 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7.5" />
        <path d="M14 3v5h5" />
        <path d="M8 13h6" />
        <path d="M8 17h4" />
        <path d="M8 9h2" />
      </svg>
    );
  }

  if (type === "analyze") {
    return (
      <svg {...props}>
        <rect x="5" y="5" width="14" height="14" rx="3" />
        <circle cx="9.5" cy="10" r="1" fill="currentColor" stroke="none" />
        <circle cx="14.5" cy="10" r="1" fill="currentColor" stroke="none" />
        <path d="M9 14.5c.8.8 1.8 1.2 3 1.2s2.2-.4 3-1.2" />
        <path d="M12 2v2" />
        <path d="M12 20v2" />
        <path d="M2 12h2" />
        <path d="M20 12h2" />
      </svg>
    );
  }

  return (
    <svg {...props}>
      <path d="M12 3l7 4v5c0 4.4-3 8.5-7 9.5C8 21.5 5 17.4 5 12V7l7-4z" />
      <path d="M9 12l2 2 4-4.5" />
    </svg>
  );
}

function StepIconBox({ type, accent }) {
  return (
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: 14,
        marginTop: 36,
        marginBottom: 18,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: accent,
        background: `linear-gradient(145deg, ${accent}22 0%, rgba(255,255,255,0.02) 100%)`,
        border: `1px solid ${accent}40`,
        boxShadow: `0 8px 24px ${accent}14`,
      }}
    >
      <StepIcon type={type} />
    </div>
  );
}

export default function LandingPage({ onValidate, onSignup, onPricing, part = "all" }) {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const showHero = part === "all" || part === "hero";
  const showRest = part === "all" || part === "rest";

  return (
    <div style={{ width: "100%" }}>
      {showHero && (
        <header
          style={{
            textAlign: "center",
            padding: "48px 20px 56px",
            maxWidth: 800,
            margin: "0 auto",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              border: `1px solid ${C.border}`,
              borderRadius: 99,
              padding: "6px 16px",
              marginBottom: 28,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.8,
              color: C.muted,
              textTransform: "uppercase",
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.green, boxShadow: `0 0 8px ${C.green}` }} />
            AI-powered VC analysis · Live
          </div>

          <h1
            style={{
              fontFamily: "Space Grotesk, sans-serif",
              fontSize: "clamp(2.25rem, 5.5vw, 3.75rem)",
              fontWeight: 700,
              lineHeight: 1.1,
              marginBottom: 24,
              letterSpacing: "-0.02em",
            }}
          >
            Is your startup idea{" "}
            <span
              style={{
                background: `linear-gradient(135deg, ${C.indigo}, ${C.violet})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              worth building?
            </span>
          </h1>

          <p style={{ fontSize: "clamp(1rem, 2vw, 1.125rem)", color: C.muted, lineHeight: 1.7, marginBottom: 36, maxWidth: 620, margin: "0 auto 36px" }}>
            Get a brutal, honest VC-style AI verdict in seconds. Our AI scores your idea across 5 dimensions — market, timing,
            competition, monetization, and defensibility — then helps you pivot, map competitors, and ship faster with Layer 2 idea development.
          </p>

          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginBottom: 40 }}>
            <GhostBtn primary onClick={onValidate || (() => scrollTo("validate"))}>
              Try a free demo →
            </GhostBtn>
            <GhostBtn onClick={() => scrollTo("how-it-works")}>See how it works</GhostBtn>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, flexWrap: "wrap" }}>
            <div style={{ display: "flex" }}>
              {["AK", "MR", "JP", "SL", "TW"].map((init, i) => (
                <div
                  key={init}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: [C.indigo, C.green, C.amber, C.violet, "#EC4899"][i],
                    border: "2px solid #080C18",
                    marginLeft: i ? -10 : 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {init}
                </div>
              ))}
            </div>
            <div style={{ textAlign: "left" }}>
              <div style={{ color: C.amber, fontSize: 14, letterSpacing: 2, marginBottom: 2 }}>★★★★★</div>
              <div style={{ fontSize: 13, color: C.muted }}>2,400+ founders already validated their ideas</div>
            </div>
          </div>
        </header>
      )}

      {showRest && (
      <>
      {/* Stats */}
      <section aria-label="Platform statistics" style={{ maxWidth: 900, margin: "0 auto 80px", padding: "0 20px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16 }}>
          {[
            { value: "24K+", label: "Ideas validated", grad: false },
            { value: "4.9/5", label: "Average rating", grad: true },
            { value: "~8s", label: "Analysis time", grad: false },
          ].map((s) => (
            <div
              key={s.label}
              style={{
                textAlign: "center",
                padding: "28px 20px",
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 16,
              }}
            >
              <div
                style={{
                  fontFamily: "Space Grotesk, sans-serif",
                  fontSize: 36,
                  fontWeight: 700,
                  marginBottom: 6,
                  ...(s.grad
                    ? { background: `linear-gradient(135deg, ${C.indigo}, ${C.violet})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }
                    : { color: C.indigo }),
                }}
              >
                {s.value}
              </div>
              <div style={{ fontSize: 14, color: C.muted }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" style={{ maxWidth: 1000, margin: "0 auto 80px", padding: "0 20px", textAlign: "center" }}>
        <SectionLabel>How it works</SectionLabel>
        <h2 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: "clamp(1.75rem, 4vw, 2.5rem)", fontWeight: 700, marginBottom: 12 }}>
          From idea to verdict in 3 steps
        </h2>
        <p style={{ color: C.muted, fontSize: 16, marginBottom: 48, maxWidth: 480, margin: "0 auto 48px" }}>
          No fluff. No generic advice. Just sharp, data-driven analysis.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20, textAlign: "left" }}>
          {STEPS.map((step) => (
            <article
              key={step.n}
              style={{
                padding: 28,
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 16,
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 20,
                  left: 20,
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: "rgba(99,102,241,0.2)",
                  color: C.indigo,
                  fontSize: 13,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {step.n}
              </div>
              <StepIconBox type={step.icon} accent={step.accent} />
              <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 18, fontWeight: 600, marginBottom: 10 }}>{step.title}</h3>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.6, margin: 0 }}>{step.text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Layer 2 — Idea Development */}
      <section
        id="idea-development"
        aria-label="Layer 2 idea development tools"
        style={{ maxWidth: 1280, margin: "0 auto 80px", padding: "0 20px", textAlign: "center" }}
      >
        <SectionLabel>Layer 2 — Idea development</SectionLabel>
        <h2 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: "clamp(1.75rem, 4vw, 2.5rem)", fontWeight: 700, marginBottom: 12 }}>
          Needs Work? Here&apos;s what to do next
        </h2>
        <p style={{ color: C.muted, fontSize: 16, marginBottom: 48, maxWidth: 640, margin: "0 auto 48px", lineHeight: 1.65 }}>
          Validation is Layer 1. When your idea needs work, Layer 2 gives you five AI tools — powered by your scores and killer insight — to pivot smarter and move toward launch.
        </p>
        <style>{`
          .layer2-tool-grid {
            display: grid;
            grid-template-columns: repeat(5, minmax(0, 1fr));
            gap: 12px;
            text-align: left;
          }
          @media (max-width: 960px) {
            .layer2-tool-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          }
          @media (max-width: 520px) {
            .layer2-tool-grid { grid-template-columns: 1fr; }
          }
        `}</style>
        <div className="layer2-tool-grid">
          {LAYER2_TOOLS.map((tool) => (
            <article
              key={tool.title}
              style={{
                padding: "20px 18px",
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 16,
                minWidth: 0,
              }}
            >
              <div style={{ fontSize: 26, marginBottom: 10 }} aria-hidden="true">{tool.icon}</div>
              <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 15, fontWeight: 600, marginBottom: 8, lineHeight: 1.3 }}>{tool.title}</h3>
              <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.55, margin: "0 0 12px" }}>{tool.text}</p>
              <span
                style={{
                  display: "inline-block",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: tool.plan === "Free" ? C.green : C.indigo,
                  padding: "4px 10px",
                  borderRadius: 99,
                  background: tool.plan === "Free" ? "rgba(16,185,129,0.12)" : "rgba(99,102,241,0.12)",
                }}
              >
                {tool.plan} plan
              </span>
            </article>
          ))}
        </div>
        <p style={{ marginTop: 32, fontSize: 14, color: C.muted }}>
          Available after <strong style={{ color: C.amber }}>Needs Work</strong> or <strong style={{ color: C.red }}>Hard Pass</strong> verdicts — sign in to unlock.
        </p>
      </section>

      {/* Live preview */}
      <section aria-label="Analysis preview" style={{ maxWidth: 640, margin: "0 auto 80px", padding: "0 20px", textAlign: "center" }}>
        <SectionLabel>Live preview</SectionLabel>
        <h2 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: "clamp(1.5rem, 3.5vw, 2rem)", fontWeight: 700, marginBottom: 8 }}>
          What your analysis looks like
        </h2>
        <p style={{ color: C.muted, fontSize: 14, marginBottom: 32 }}>
          Real output for &ldquo;AI-powered dog walking marketplace&rdquo;
        </p>
        <article
          style={{
            textAlign: "left",
            padding: 28,
            background: "rgba(255,255,255,0.03)",
            border: `1px solid ${C.border}`,
            borderRadius: 20,
          }}
        >
          <p style={{ fontStyle: "italic", color: C.muted, fontSize: 14, marginBottom: 24, lineHeight: 1.5 }}>
            &ldquo;An AI-powered marketplace connecting dog owners with vetted walkers&hellip;&rdquo;
          </p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 16 }}>
            <span
              style={{
                padding: "10px 20px",
                borderRadius: 10,
                border: `2px solid ${C.green}`,
                color: C.green,
                fontFamily: "Space Grotesk, sans-serif",
                fontWeight: 700,
                fontSize: 18,
              }}
            >
              FUND IT
            </span>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 48, fontWeight: 700, color: C.green, lineHeight: 1 }}>84</div>
              <div style={{ fontSize: 12, color: C.muted }}>overall score</div>
            </div>
          </div>
          {PREVIEW_SCORES.map((s) => (
            <ScoreBar key={s.label} {...s} />
          ))}
          <div
            style={{
              marginTop: 24,
              padding: 20,
              borderRadius: 12,
              background: "rgba(0,0,0,0.25)",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ filter: "blur(4px)", opacity: 0.4, fontSize: 13, color: C.muted, lineHeight: 2 }}>
              Bull case analysis&hellip; Bear case risks&hellip; Killer insight&hellip;
            </div>
            <p style={{ fontSize: 13, color: C.muted, margin: "12px 0 16px" }}>Bull case, Bear case &amp; Killer insight locked</p>
            <GhostBtn primary onClick={onValidate || (() => scrollTo("validate"))}>
              See full analysis — it&apos;s free →
            </GhostBtn>
          </div>
        </article>
      </section>

      {/* Testimonials */}
      <section aria-label="Customer testimonials" style={{ maxWidth: 1100, margin: "0 auto 80px", padding: "0 20px", textAlign: "center" }}>
        <SectionLabel>Testimonials</SectionLabel>
        <h2 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: "clamp(1.75rem, 4vw, 2.5rem)", fontWeight: 700, marginBottom: 8 }}>
          What founders say
        </h2>
        <p style={{ color: C.muted, fontSize: 16, marginBottom: 40 }}>From solo builders to YC applicants</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, textAlign: "left" }}>
          {TESTIMONIALS.map((t) => (
            <blockquote
              key={t.name}
              style={{
                margin: 0,
                padding: 28,
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 16,
              }}
            >
              <div style={{ color: C.amber, fontSize: 14, letterSpacing: 2, marginBottom: 16 }}>★★★★★</div>
              <p style={{ fontSize: 15, fontStyle: "italic", lineHeight: 1.65, marginBottom: 24, color: C.text }}>&ldquo;{t.quote}&rdquo;</p>
              <footer style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: t.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {t.name.split(" ")[0][0]}
                  {t.name.split(" ")[1]?.[0]}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{t.role}</div>
                </div>
              </footer>
            </blockquote>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing plans" style={{ maxWidth: 1000, margin: "0 auto 80px", padding: "0 20px", textAlign: "center" }}>
        <SectionLabel>Pricing</SectionLabel>
        <h2 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: "clamp(1.75rem, 4vw, 2.5rem)", fontWeight: 700, marginBottom: 8 }}>
          Start free, scale when ready
        </h2>
        <p style={{ color: C.muted, fontSize: 16, marginBottom: 48 }}>No credit card required to get started</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20, alignItems: "stretch" }}>
          {PRICING.map((plan) => (
            <article
              key={plan.name}
              style={{
                padding: 28,
                background: C.surface,
                border: `2px solid ${plan.popular ? C.indigo : C.border}`,
                borderRadius: 16,
                textAlign: "left",
                position: "relative",
                boxShadow: plan.popular ? "0 0 40px rgba(99,102,241,0.15)" : "none",
              }}
            >
              {plan.popular && (
                <div
                  style={{
                    position: "absolute",
                    top: -12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: `linear-gradient(135deg, ${C.indigo}, ${C.violet})`,
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "4px 14px",
                    borderRadius: 99,
                    letterSpacing: 0.5,
                  }}
                >
                  MOST POPULAR
                </div>
              )}
              <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 18, color: plan.popular ? C.indigo : C.text, marginBottom: 8 }}>{plan.name}</h3>
              <div style={{ marginBottom: 24 }}>
                <span style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 40, fontWeight: 700 }}>{plan.price}</span>
                <span style={{ color: C.muted, fontSize: 14 }}> {plan.period}</span>
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px" }}>
                {plan.features.map((f) => (
                  <li key={f} style={{ fontSize: 14, color: C.muted, marginBottom: 10, paddingLeft: 22, position: "relative" }}>
                    <span style={{ position: "absolute", left: 0, color: plan.popular ? C.indigo : C.green }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              <GhostBtn
                primary={plan.popular}
                onClick={() => (plan.name === "Free" ? (onSignup ? onSignup() : scrollTo("validate")) : onPricing ? onPricing() : scrollTo("validate"))}
              >
                {plan.cta}
              </GhostBtn>
            </article>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" aria-label="Frequently asked questions" style={{ maxWidth: 1000, margin: "0 auto 24px", padding: "0 20px" }}>
        <SectionLabel>FAQ</SectionLabel>
        <h2 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: "clamp(1.75rem, 4vw, 2.5rem)", fontWeight: 700, marginBottom: 12, textAlign: "center" }}>
          Common questions
        </h2>
        <p style={{ color: C.muted, fontSize: 16, marginBottom: 40, textAlign: "center" }}>
          Everything you need to know about validating startup ideas with AI.
        </p>
        <div>
          {FAQ.map((item) => (
            <article
              key={item.q}
              itemScope
              itemType="https://schema.org/Question"
              style={{
                marginBottom: 16,
                padding: "20px 22px",
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 14,
              }}
            >
              <h3 itemProp="name" style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 16, fontWeight: 600, marginBottom: 10 }}>
                {item.q}
              </h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p itemProp="text" style={{ fontSize: 14, color: C.muted, lineHeight: 1.65, margin: 0 }}>
                  {item.a}
                </p>
              </div>
            </article>
          ))}
        </div>
        <p style={{ textAlign: "center", marginTop: 24, fontSize: 14, color: C.muted }}>
          <a href="/faq.html" style={{ color: C.indigo, textDecoration: "none" }}>
            View full FAQ page →
          </a>
        </p>
      </section>

      {/* Final CTA */}
      <section
        aria-label="Get started"
        style={{
          textAlign: "center",
          padding: "32px 20px 80px",
          maxWidth: 640,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            border: `1px solid rgba(99,102,241,0.35)`,
            borderRadius: 99,
            padding: "6px 16px",
            marginBottom: 28,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.8,
            color: C.indigo,
            textTransform: "uppercase",
          }}
        >
          No credit card required
        </div>
        <h2 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: "clamp(2rem, 5vw, 3rem)", fontWeight: 700, marginBottom: 16, lineHeight: 1.15 }}>
          Stop guessing.
          <br />
          Start validating.
        </h2>
        <p style={{ color: C.muted, fontSize: 16, marginBottom: 32 }}>3 free validations. No credit card. Takes 30 seconds.</p>
        <GhostBtn primary onClick={onValidate || (() => scrollTo("validate"))}>
          Validate my idea for free →
        </GhostBtn>
        <p style={{ fontSize: 12, color: C.muted, marginTop: 24 }}>
          7-day money-back guarantee · Cancel anytime · GDPR compliant
        </p>
      </section>

      {/* SEO footer */}
      <footer
        style={{
          borderTop: `1px solid ${C.border}`,
          padding: "32px 20px 48px",
          maxWidth: 900,
          margin: "0 auto",
          textAlign: "center",
        }}
      >
        <p style={{ fontFamily: "Space Grotesk, sans-serif", fontWeight: 700, fontSize: 16, marginBottom: 12 }}>AI Startup Validator</p>
        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.7, maxWidth: 560, margin: "0 auto 16px" }}>
          The fastest way to validate startup ideas with AI. Get VC-style scores for market size, timing, competition,
          monetization, and defensibility — then pivot, map competitors, and build with Layer 2 idea development tools.
        </p>
        <nav style={{ fontSize: 13, color: C.muted, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "8px 16px" }}>
          <a href="#validate" style={{ color: C.indigo, textDecoration: "none" }}>Validate idea</a>
          <a href="/how-it-works.html" style={{ color: C.indigo, textDecoration: "none" }}>How it works</a>
          <a href="/pricing.html" style={{ color: C.indigo, textDecoration: "none" }}>Pricing</a>
          <a href="/faq.html" style={{ color: C.indigo, textDecoration: "none" }}>FAQ</a>
          <a href="/privacy.html" style={{ color: C.indigo, textDecoration: "none" }}>Privacy</a>
          <a href="/terms.html" style={{ color: C.indigo, textDecoration: "none" }}>Terms</a>
          <a href="mailto:support@aistartupvalidator.com" style={{ color: C.indigo, textDecoration: "none" }}>Support</a>
        </nav>
        <p style={{ fontSize: 12, color: C.muted, marginTop: 20 }}>© {new Date().getFullYear()} AI Startup Validator. All rights reserved.</p>
      </footer>
      </>
      )}
    </div>
  );
}
